import Vue from 'vue'
import Router from 'vue-router'

Vue.use(Router)

// Meta fields:
// -displayName = The human-readable name to render in a navigation area.
// -auth = The authentication requirement for viewing the page. If false or not
//         set, no authentication is required. If true, a login of any sort is
//         required. See https://github.com/websanova/vue-auth/blob/master/docs/Methods.md#check
//         for more detailed options.
// -setup = true if a user is required to have completed the setup process before
//          viewing the page; false or unset to have no setup requirement. If
//          set to true without any auth requirement, it will be ignored for
//          unauthenticated requests.
// -ignoreEmailConfirmation = true if we should skip checking whether or not the
//                            user has confirmed their auth_local email.
// -subscriptionRequired = true if a user must have an active subscription before
//                         being allowed to access the page.
// -simplePath = A more basic form of the route path to use for navigation instead
//               of "path" (useful if path contains params and/or regex).
// -isSubpage = The route is not a top-level page but is meant to be navigated to
//              through another page. This will hide the route from top-level
//              navigation.
// -parent = The route path that is the parent of the current path (used for
//           navigating back a level). Can be null to indicate that no navigation
//           should be available from the route.
// -hideNav = true if the navigation bar should be hidden from rendering. false
//            (default) will keep the navigation bar visible.
const routerSetup = [
  { path: '/',
    component: 'Home',
    meta: {
      displayName: null,
      setup: true,
      parent: null
    }
  },
  { path: '/previous',
    component: 'Previous',
    meta: {
      displayName: 'Previous',
      auth: 'church-member',
      setup: true
    }
  },
  { path: '/devotional/:id(\\d+)?',
    component: 'Devotional',
    meta: {
      displayName: 'Today',
      auth: 'church-member',
      setup: true,
      simplePath: '/devotional'
    }
  },
  { path: '/progress',
    component: 'Progress',
    meta: {
      displayName: null,
      auth: 'church-member',
      setup: true
    }
  },
  { path: '/diary',
    component: 'Diary',
    meta: {
      displayName: null,
      auth: 'church-member',
      setup: true
    }
  },
  { path: '/diary/:id(\\d+)',
    component: 'Diary-Single',
    meta: {
      displayName: null,
      auth: 'church-member',
      setup: true
    }
  },
  { path: '/diary/new',
    component: 'Diary-New',
    meta: {
      displayName: null,
      auth: 'church-member',
      setup: true
    }
  },
  { path: '/distractions',
    component: 'Distractions',
    meta: {
      displayName: 'Distractions',
      auth: 'church-member',
      setup: true
    }
  },
  { path: '/setup',
    component: 'Setup',
    meta: {
      displayName: null,
      auth: true,
      parent: null
    }
  },
  { path: '/confirm/:token?',
    component: 'Confirm',
    meta: {
      simplePath: '/confirm',
      displayName: null,
      auth: true,
      ignoreEmailConfirmation: true,
      parent: null
    }
  },
  { path: '/forgot',
    component: 'Forgot',
    meta: {
      displayName: null,
      auth: false,
      parent: '/login'
    }
  },
  { path: '/forgot/finish/:token?',
    component: 'Forgot-Finish',
    meta: {
      simplePath: '/forgot/finish',
      displayName: null,
      auth: false,
      parent: '/login'
    }
  },
  { path: '/account',
    component: 'Account',
    meta: {
      displayName: null,
      auth: true,
      setup: true
    }
  },
  { path: '/login',
    component: 'Login',
    meta: {
      displayName: 'Log In',
      auth: false,
      hideNav: true
    }
  },
  { path: '/logout',
    component: 'Logout',
    meta: {
      displayName: null,
      auth: true,
      ignoreEmailConfirmation: true
    }
  },
  { path: '/oauth2',
    component: 'Login-OAuth2',
    meta: {
      displayName: null
    }
  },
  { path: '/signup',
    component: 'Signup',
    meta: {
      displayName: 'Sign Up',
      auth: false,
      hideNav: true
    }
  },
  { path: '/subscribe/:action(approve|cancel)?',
    component: 'Subscribe',
    meta: {
      displayName: null,
      auth: true
    }
  },
  { path: '/subscriptions/:id(\\d+)?',
    component: 'Subscriptions',
    meta: {
      displayName: null,
      auth: true,
      setup: true
    }
  },
  { path: '/invoices/:id(\\d+)',
    component: 'Invoices-Single',
    meta: {
      displayName: null,
      auth: true,
      setup: true,
      parent: '/subscriptions'
    }
  },
  { path: '/invoices/payment/:action(approve|cancel)',
    component: 'Invoices-Payment',
    meta: {
      displayName: null,
      auth: true,
      parent: null
    }
  },
  { path: '/admin',
    component: 'admin/Index',
    meta: {
      displayName: 'Admin Page',
      auth: 'admin'
    }
  },
  { path: '/admin/users',
    component: 'admin/Users',
    meta: {
      displayName: 'Users',
      auth: 'admin',
      isSubpage: true
    }
  },
  { path: '/admin/users/:id(\\d+|new)',
    component: 'admin/Users-Single',
    meta: {
      displayName: null,
      auth: 'admin'
    }
  },
  { path: '/admin/pricing',
    component: 'admin/Pricing',
    meta: {
      displayName: 'Pricing and Billing',
      auth: 'admin',
      isSubpage: true
    }
  },
  { path: '/admin/subscriptions',
    component: 'admin/Subscriptions',
    meta: {
      displayName: 'Subscriptions',
      auth: 'admin',
      isSubpage: true
    }
  },
  { path: '/admin/subscriptions/:id(\\d+|new)',
    component: 'admin/Subscriptions-Single',
    meta: {
      displayName: null,
      auth: 'admin'
    }
  },
  { path: '/admin/subscriptions/:id(\\d+)/holds',
    component: 'admin/Subscriptions-Holds',
    meta: {
      displayName: null,
      auth: 'admin',
      parent: '/admin/subscriptions'
    }
  },
  { path: '/admin/subscriptions/:id(\\d+)/invoices',
    component: 'admin/Subscriptions-Invoices',
    meta: {
      displayName: null,
      auth: 'admin',
      parent: '/admin/subscriptions'
    }
  },
  { path: '/admin/churches',
    component: 'admin/Churches',
    meta: {
      displayName: 'Churches',
      auth: 'admin',
      isSubpage: true
    }
  },
  { path: '/admin/churches/:id(\\d+|new)',
    component: 'admin/Churches-Single',
    meta: {
      displayName: null,
      auth: 'admin'
    }
  },
  { path: '/admin/devotionals',
    component: 'admin/Devotionals',
    meta: {
      displayName: 'Devotionals',
      auth: 'admin',
      isSubpage: true
    }
  },
  { path: '/admin/devotionals/:id(\\d+|new)',
    component: 'admin/Devotionals-Single',
    meta: {
      displayName: null,
      auth: 'admin'
    }
  },
  { path: '/admin/devotionals/sets/:id(\\d+|new)',
    component: 'admin/DevotionalSets-Single',
    meta: {
      displayName: null,
      auth: 'admin'
    }
  },
  { path: '/admin/devotionals/marker',
    component: 'admin/Devotionals-Marker',
    meta: {
      displayName: null,
      auth: 'admin'
    }
  },
  { path: '/admin/devotional-completion-ranks',
    component: 'admin/DevotionalCompletionRanks',
    meta: {
      displayName: 'Devotional Completion Ranks',
      auth: 'admin',
      isSubpage: true
    }
  },
  { path: '/admin/devotional-completion-ranks/:id(\\d+|new)',
    component: 'admin/DevotionalCompletionRanks-Single',
    meta: {
      displayName: null,
      auth: 'admin'
    }
  },
  { path: '/admin/distractions',
    component: 'admin/Distractions',
    meta: {
      displayName: 'Distractions',
      auth: 'admin',
      isSubpage: true
    }
  },
  { path: '/admin/distractions/:id(\\d+|new)',
    component: 'admin/Distractions-Single',
    meta: {
      displayName: null,
      auth: 'admin'
    }
  },
  { path: '/admin/distractions/types/:id(\\d+|new)',
    component: 'admin/DistractionTypes-Single',
    meta: {
      displayName: null,
      auth: 'admin'
    }
  },
  { path: '/manage',
    component: 'manage/Index',
    meta: {
      displayName: 'Management Page',
      auth: [ 'church-staff' ]
    }
  },
  { path: '/manage/church/:id(\\d+)?',
    component: 'manage/Church',
    meta: {
      displayName: null,
      auth: [ 'admin', 'church-staff' ],
      isSubpage: true
    }
  },
  { path: '/privacy',
    component: 'Privacy',
    meta: {
      displayName: null
    }
  },
  { path: '/terms',
    component: 'Terms',
    meta: {
      displayName: null
    }
  },
  { path: '/403',
    component: '403',
    meta: {
      displayName: null
    }
  },
  // Final catch-all route, must come at the end
  { path: '*',
    component: '404',
    meta: {
      displayName: null,
      parent: '/'
    }
  }
]
// Add any environment-specific routes
if (process.env.NODE_ENV === 'development') {
  routerSetup.unshift({
    path: '/debug',
    component: 'Debug',
    meta: {
      displayName: 'Debugging'
    }
  })
}
const routes = routerSetup.map(route => {
  return {
    ...route,
    component: () => import(`@/components/pages/${route.component}.vue`)
  }
})

export default new Router({
  mode: 'history',
  routes
})
